import { InvestOffer } from '../types';

// Mocked ratings keyed by lwin18 so Phase 1 rows have plausible filterable data
// before the W5 wine-detail backend lookup lands. Deterministic per wine.
export interface RowRatings {
  vintage: number;
  critic: number;
  fairValue: number;
  liquidity: number;
  performance: number;
  grade: number;
}

export function getRowRatings(lwin18: string | undefined | null): RowRatings {
  const hash = (lwin18 ?? '').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return {
    vintage: 85 + (hash % 15),
    critic: 88 + ((hash * 3) % 13),
    fairValue: 85 + ((hash * 7) % 16),
    liquidity: 80 + ((hash * 11) % 20),
    performance: 70 + ((hash * 13) % 25),
    grade: 4 + ((hash * 17) % 7),
  };
}

// Editorially-maintained iconic producer list. Hardcoded for Phase 1;
// migrates to Prismic / admin tool in a follow-up.
export const ICONIC_PRODUCER_LIST = [
  'Domaine de la Romanée-Conti',
  'Domaine Leroy',
  'Domaine Leflaive',
  'Coche-Dury',
  'Château Petrus',
  'Château Cheval Blanc',
  'Château Lafite Rothschild',
  'Château Mouton Rothschild',
  'Château Margaux',
  'Château Haut-Brion',
  'Château Latour',
  'Krug',
  'Salon',
  'Dom Pérignon',
  'Vega Sicilia',
  'Sassicaia',
];

type RowKeyId =
  | 'under-market'
  | 'new-releases'
  | 'allocations'
  | 'great-fair-value'
  | 'curated-parcels'
  | 'iconic-wines';

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// Live ratings preferred; synthetic hash falls in for wines without a CW Ratings record.
function ratingFor(offer: InvestOffer, ratingsMap?: Map<string, RowRatings>): RowRatings {
  const lwin11 = offer.lwin18 ? offer.lwin18.substring(0, 11) : '';
  const live = ratingsMap?.get(lwin11);
  return live ?? getRowRatings(offer.lwin18);
}

function isUnderMarket(offer: InvestOffer, r: RowRatings): boolean {
  return (
    offer.priceGbp > 0 &&
    offer.marketValue > 0 &&
    offer.priceGbp < offer.marketValue &&
    r.liquidity >= 93 &&
    r.grade >= 6
  );
}

function discountPct(offer: InvestOffer): number {
  if (!offer.marketValue) return 0;
  return (offer.marketValue - offer.priceGbp) / offer.marketValue;
}

function isNewRelease(offer: InvestOffer, now = Date.now()): boolean {
  if (!offer.orderCreatedDateTime) return false;
  const created = new Date(offer.orderCreatedDateTime).getTime();
  if (Number.isNaN(created)) return false;
  return now - created <= NINETY_DAYS_MS;
}

function isAllocation(offer: InvestOffer): boolean {
  // Phase 1 has no allocation type discriminator in offer data yet — row stays empty.
  return offer.orderType?.toLowerCase() === 'allocation';
}

function isGreatFairValue(offer: InvestOffer, r: RowRatings): boolean {
  return r.fairValue >= 95 && r.liquidity >= 90 && offer.priceGbp < 1000;
}

function isCuratedParcel(offer: InvestOffer): boolean {
  // Phase 3 delivers parcels. Row stays empty in Phase 1.
  return offer.orderType?.toLowerCase() === 'parcel';
}

function isIconic(offer: InvestOffer, r: RowRatings): boolean {
  if (r.grade >= 10) return true;
  const name = (offer.name ?? offer.wineName ?? '').toLowerCase();
  return ICONIC_PRODUCER_LIST.some((p) => name.includes(p.toLowerCase()));
}

export function buildRows(
  offers: InvestOffer[],
  ratingsMap?: Map<string, RowRatings>,
): Record<RowKeyId, InvestOffer[]> {
  const ratingOf = (o: InvestOffer) => ratingFor(o, ratingsMap);

  const underMarket = offers
    .filter((o) => isUnderMarket(o, ratingOf(o)))
    .sort((a, b) => discountPct(b) - discountPct(a))
    .slice(0, 6);

  const newReleases = offers
    .filter((o) => isNewRelease(o))
    .sort((a, b) => {
      const at = new Date(a.orderCreatedDateTime).getTime();
      const bt = new Date(b.orderCreatedDateTime).getTime();
      if (bt !== at) return bt - at;
      return ratingOf(b).critic - ratingOf(a).critic;
    })
    .slice(0, 8);

  const allocations = offers.filter(isAllocation).slice(0, 6);

  const greatFairValue = offers
    .filter((o) => isGreatFairValue(o, ratingOf(o)))
    .sort((a, b) => {
      const ra = ratingOf(a);
      const rb = ratingOf(b);
      if (rb.fairValue !== ra.fairValue) return rb.fairValue - ra.fairValue;
      return discountPct(b) - discountPct(a);
    })
    .slice(0, 8);

  const curatedParcels = offers.filter(isCuratedParcel).slice(0, 6);

  const iconicWines = offers.filter((o) => isIconic(o, ratingOf(o))).slice(0, 8);

  return {
    'under-market': underMarket,
    'new-releases': newReleases,
    allocations,
    'great-fair-value': greatFairValue,
    'curated-parcels': curatedParcels,
    'iconic-wines': iconicWines,
  };
}
